/**
 * ConversationDetails.js
 * Talkroute
 *
 * Created by Vel Genov on 3/25/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */

import React, { useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Grid, List } from "semantic-ui-react";

import { push } from "connected-react-router";
import ReadIndicator from "../voicemailComponents/ReadIndicator";
import uiString from "../../constants/uiString";
import {
  hideConversationDetails,
  findConversationById,
  updateMessageCount,
  updateReadStatus,
  deleteConversation,
  getConversationDownload,
} from "../../actions/conversationActions";
import { ConversationData, WELCOME_CONVERSATION, conversationJSONParser } from "../../services/dataClasses/ConversationData";
import {
  setOutgoingPhoneNumber,
  setNumberToCall,
  fetchBlockedNumbers,
  blockNumber,
  unblockNumber,
} from "../../actions/callActions";
import { getUserPermissions } from "../../actions/userActions";
import CallDetails from "../../containers/CallDetails";
import { formatPhoneNumber } from "../../services/dataClasses/CallData";
import routes, { routeUrlBuilders } from "../../constants/routes";
import DeleteIcon from "@resources/icons/top-nav-delete.svg";
import CallBackIcon from "@resources/icons/top-nav-call-back.svg";
import BackIcon from "@resources/icons/top-nav-go-back.svg";
import ConfirmModal from "../modal/ConfirmModal";
import UserPermissionsData from "../../services/dataClasses/UserPermissionsData";
import userConstants from "../../constants/userConstants";
import CallBlockIcon from "@resources/icons/top-nav-block.svg";
import CallUnblockIcon from "@resources/icons/top-nav-unblock.svg";
import DownloadButton from "@resources/icons/arrow-download-button.svg";
import ConfirmDialog from "@components/PresentationComponents/ConfirmDialog";
import { IconButton } from "@material-ui/core";
import { useAppSelector } from "@helpers/hooks/useAppSelector.hook";
import useLocalStorage, { localStorageItems } from "@helpers/hooks/useLocalStorage.hook";
import { IUserMetadata } from "@models/LocalStorageItems.models";
import Tooltip from '@components/PresentationComponents/FormComponents/Tooltip';
import CreateContactDialog from "@components/Contacts/CreateContactDialog";
import { ContactPhoneTypeEnum, IContact } from "@models/Contacts.models";
import CreateContactIcon from "@resources/icons/ic-add-contact.svg";
import AddContactIcon from "@resources/icons/phone-plus.svg";
import ContactDialog from "@components/Contacts/ContactDialog";
import { useGetAllContactsQuery } from "@services/kingcobraApi";
import { ADD_CONTACT, ADD_CONTACT_TOOLTIP_LABEL, CONTACTS_INITIAL_PAGE, GET_TEN_THOUSAND_CONTACTS } from "@components/Contacts/constants";
import { addContactNameToConversation } from "@helpers/functions/contacts";
import { getTextMessageAllowedForNumber, getEditableForNumber, TalkroutePhoneNumber } from "@services/dataClasses/TalkroutePhoneNumber";

/**
 * Place the delete confirmation into the view
 */
 export const DeleteConversationDialog = ({ 
  open,
  onDelete, 
  onClose, 
  currentConversationId, 
  conversations }) => {
  const userId = useAppSelector(state => state.authentication.userId);
  const [metadata, setMetadata] = useLocalStorage<IUserMetadata[]>(localStorageItems.metadata, []);
  const currentConversation = conversations.find(
    (conversation) => conversation.conversationId === currentConversationId
  );
  const contactNumber = currentConversation ? currentConversation.contactNumber : null;
  const formattedPhoneNumber = contactNumber ? formatPhoneNumber(contactNumber) : "";
  const handleConfirmation = () => {
    if (currentConversationId === WELCOME_CONVERSATION) {
      const existingUserDataIndex = metadata.findIndex(userData => userData.userId === userId);
      if (existingUserDataIndex !== -1) {
        const newMetadata = metadata.map((userData, index) => 
          index === existingUserDataIndex ? ({...userData, isWelcomeConversationDeleted: true}) : userData
        )
        setMetadata(newMetadata)
      } else {
        setMetadata([...metadata, {
          userId: userId as number,
          isWelcomeVoicemailDeleted: false,
          isWelcomeVoicemailRead: false,
          isWelcomeConversationDeleted: true,
          isWelcomeConversationRead: false,
        }])
      }
    }
    onDelete();
  }

  return (
    <ConfirmDialog
      header={`${uiString.CONVERSATION_DELETE_CONFIRMATION_HEADER} - ${formattedPhoneNumber}`}
      content={uiString.CONVERSATION_DELETE_CONFIRMATION_BODY}
      open={open}
      confirmButtonLabel={uiString.DELETE}
      onCancel={onClose}
      onConfirm={handleConfirmation}
    />
  );
}

const overflowStyle = {
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  overflow: "hidden",
};

const buttonStyles = (isDisabled?: boolean) => ({
  padding: 10,
  bottom: -3,
  right: 1,
  opacity: isDisabled ? .45 : 1
})

interface IConversationDetails {
  conversationDetails?: string;
  relatedCalls?: any[];
  parent?: string;
  conversations: any[];
  currentConversationId?: string;
  blockedNumbers: any[];
  userPermissions?: UserPermissionsData;
  outgoingPhoneNumbers: TalkroutePhoneNumber[],
  temporaryConversation: ConversationData | null;
  // redux actions
  hideConversationDetails: () => any;
  updateMessageCount: ({conversations, conversationId} : { conversations: any, conversationId: string }) => any;
  updateReadStatus: ({conversationId, readStatus} : { conversationId: string, readStatus: boolean }) => any;
  deleteConversation: (conversationId?: string) => any;
  setOutgoingPhoneNumber: (number: string) => any;
  setNumberToCall: (number: string) => any;
  fetchBlockedNumbers: () => any;
  getUserPermissions: () => any;
  blockNumber: (number: string) => any;
  unblockNumber: (number: string) => any;
  push: (path: string) => any;
}

  /**
   * Display details about a conversation
   *
   */
  class ConversationDetails extends React.Component<IConversationDetails, any> {
    static defaultProps = {
      relatedCalls: [],
      conversations: [],
      blockedNumbers: [],
      outgoingPhoneNumbers: [],
    };
    /**
     * Request a message count update, if not available
     */
    requestMessageCountUpdate = () => {
      const {
        conversations,
        conversationDetails,
        updateMessageCount: updateMessageCountLocal,
      } = this.props;

      updateMessageCountLocal({
        conversations,
        conversationId: conversationDetails as any,
      });
    };

    /**
     * Get a call ready. Pass the numberToCall, outgoingPhoneNumber to Keypad
     */
    callBack = () => {
      const {
        conversations,
        currentConversationId,
        setOutgoingPhoneNumber: setOutgoingPhoneNumberLocal,
        setNumberToCall: setNumberToCallLocal,
        push,
      } = this.props;

      if (conversations) {
        const currentConversation = conversations.find(
          (conversation) => conversation.conversationId === currentConversationId
        );

        if (currentConversation) {
          const { contactNumber, talkrouteNumber } = currentConversation;

          setOutgoingPhoneNumberLocal(talkrouteNumber);
          setNumberToCallLocal(contactNumber);
          push(routes.KEYPAD);
        }
      }
    };
    
    /**
     * Block/unblock the call for phone number
     */
    callBlock = () => {
      const {
        userPermissions,
        blockedNumbers,
        conversations,
        currentConversationId,
        blockNumber: blockNumberLocal,
        unblockNumber: unblockNumberLocal,
      } = this.props;

      if (conversations) {
        const currentConversation = conversations.find(
          (conversation) => conversation.conversationId === currentConversationId
        );

        if (currentConversation) {
          const { contactNumber } = currentConversation;

          if (userPermissions) {
            const { itemBlockedNumber } = userPermissions;

            if (itemBlockedNumber === userConstants.permissions.EDIT) {
              // Set the blocked numbers
              const blocked = blockedNumbers.some(
                (number) => number === contactNumber
              );

              if (blocked) {
                return unblockNumberLocal(contactNumber);
              }
              return blockNumberLocal(contactNumber);
            }
          }

          this.setState({ isShowBlockPermissionError: true });
        }
      }
    };

    /**
     * Called to open the context popup menu
     */
    popupOpen = () => this.setState({ popupOpen: true });

    /**
     * Called to close the context popup menu
     */
    popupClose = () => this.setState({ popupOpen: false });

    /**
     * Show the call block permission error dialog
     */
    showBlockPermissionError = () => {
      const { isShowBlockPermissionError } = this.state;

      if (isShowBlockPermissionError) {
        return (
          <ConfirmModal
            title={uiString.blockPermissionErrorModal.TITLE}
            desc={uiString.blockPermissionErrorModal.DESC}
            buttonLabel={uiString.OK}
            clickActionCallback={() =>
              this.setState({ isShowBlockPermissionError: false })
            }
          />
        );
      }

      return null;
    };

    /**
     * Request to delete a conversation with Id
     *
     */
    requestToDeleteConversation = () => {
      this.popupOpen();
    };

      /**
   * Download CSV of converstation
   */
    csvDownload = () => {
      const { currentConversationId } = this.props;

      getConversationDownload(currentConversationId)
      .catch(error => {
        console.error('Error downloading CSV:', error);
      });
    };

    /**
     * Delete this conversation
     */
    deleteConversation = () => {
      const {
        currentConversationId,
        deleteConversation: deleteConversationLocal,
        conversations,
        temporaryConversation,
        push
      } = this.props;
      this.popupClose();
      this.hideConversationDetails();
      deleteConversationLocal(currentConversationId);
      conversations.length && push(routeUrlBuilders.OPEN_CONVERSATION(conversations[0].conversationId))
    };

    /**
     * Cancel deleting this conversation
     */
    cancelDeletingConversation = () => {
      this.popupClose();
    };

    drawHeaderButtons = () => (
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {this.goBackButton()}
        {this.callBackButton()}
        {this.callBlockButton()}
        {!this.state.alreadyInContacts && this.addContactButton()}
        {this.createContactButton()}
        {this.createDownloadButton()}
        {this.deleteButton()}
      </div>
    );

    /**
     * Method to check is text messaging allowed
     */
    getIsEditable = () => {
      const { conversations, currentConversationId } = this.props;
      let currentPhoneNumber = ""; 
    
      if (conversations && currentConversationId) {
        const currentConversation = conversations.find(
          (conversation) => conversation.conversationId === currentConversationId
        );
        if (currentConversation) {
          currentPhoneNumber = currentConversation.talkrouteNumber; 
        }
      }
    
      return getTextMessageAllowedForNumber(currentPhoneNumber, this.props.outgoingPhoneNumbers);
    }

    /**
     * Method to check if calls can be made
     */
    getCanCall = () => {
      const { conversations, currentConversationId } = this.props;
      let currentPhoneNumber = ""; 
    
      if (conversations && currentConversationId) {
        const currentConversation = conversations.find(
          (conversation) => conversation.conversationId === currentConversationId
        );
        if (currentConversation) {
          currentPhoneNumber = currentConversation.talkrouteNumber; 
        }
      }
      
      return getEditableForNumber(currentPhoneNumber, this.props.outgoingPhoneNumbers);
    }

    /**
     * Draw the go back button
     */
    goBackButton = () => (
      <Tooltip title="Back">
        <IconButton
          onClick={this.hideConversationDetails}
          style={buttonStyles()}
        >
          <BackIcon />
        </IconButton>
      </Tooltip>
    );

    /**
     * Draw the call back button
     */
    callBackButton = () =>  {

      const canCall = this.getCanCall();

      return (
        <Tooltip title="Call">
          <IconButton
            onClick={() => this.callBack()}
            style={buttonStyles(this.state.isDefaultMessageSelected || !canCall)}
            disabled={this.state.isDefaultMessageSelected || !canCall}
          >
            <CallBackIcon />
          </IconButton>
        </Tooltip>
      );
    };

    /**
     * Draw the add contact button 
     */
    addContactButton = () => (
      <Tooltip title={ADD_CONTACT_TOOLTIP_LABEL}>
        <IconButton
          onClick={() => this.setState({ isContactDialogOpen: true })}
          style={buttonStyles(this.state.isDefaultMessageSelected)}
          disabled={this.state.isDefaultMessageSelected}
        >
          <AddContactIcon />
        </IconButton>
      </Tooltip>
    );

    /**
     * Draw the create contact button 
     */
    createContactButton = () => (
      <Tooltip title={this.state.alreadyInContacts ? "Edit Contact" : "Create Contact"}>
        <IconButton
          onClick={() => this.setState({ isCreateContactDialogOpen: true })}
          style={buttonStyles(this.state.isDefaultMessageSelected)}
          disabled={this.state.isDefaultMessageSelected}
        >
          <CreateContactIcon style={{ width: 19, height: 19 }} />
        </IconButton>
      </Tooltip>
      );

    /**
     * Draw the call block button
     */
    callBlockButton = () => {
      const { conversations, currentConversationId, blockedNumbers } = this.props;
      const { isDefaultMessageSelected } = this.state;

      let blocked = false;
      if (conversations) {
        const currentConversation = conversations.find(
          (conversation) => conversation.conversationId === currentConversationId
        );

        if (currentConversation) {
          const { contactNumber } = currentConversation;

          blocked = blockedNumbers.some((number) => number === contactNumber);
        }
      }

      return (
        <Tooltip title={blocked ? 'Unblock' : 'Block'}>
          <IconButton
            disabled={isDefaultMessageSelected}
            onClick={() => this.callBlock()}
            style={buttonStyles(isDefaultMessageSelected)}
          >
            {blocked ? <CallUnblockIcon /> : <CallBlockIcon />}
          </IconButton>
        </Tooltip>
      );
    };

      /**
   * Draw the download conversation button 
   */
    createDownloadButton = () => {
      const { isDefaultMessageSelected } = this.state;

      return (
        <Tooltip title="Download Conversation">
          <IconButton
            onClick={() => this.csvDownload()}
            style={buttonStyles(isDefaultMessageSelected)}
            disabled={isDefaultMessageSelected}
          >
            <DownloadButton style={{ width: 19, height: 19 }} />
          </IconButton>
        </Tooltip>
      );
    }

    /**
     * Draw the delete button
     */
    deleteButton = () => {

      const isEditable = this.getIsEditable();

      return (
        <Tooltip title="Delete">
          <IconButton
            onClick={() => this.requestToDeleteConversation()}
            style={buttonStyles(!isEditable)}
            disabled={!isEditable}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      );
    };

    /**
     * Request to mark the conversation as read/unread
     * Currently marking conversations as read only
     */
    toggleReadStatus = () => {
      const {
        updateReadStatus: updateReadStatusLocal,
        currentConversationId: conversationId,
      } = this.props;

      updateReadStatusLocal({ conversationId: conversationId as any, readStatus: true });
    };

    /**
     * Draw the read indicator button
     */
    readIndicatorButton = () => {
      const { currentConversationId, conversations, userPermissions } = this.props;
      if (!currentConversationId || !conversations) {
        return null;
      }

      const currentConversation = conversations.find(
        (conversation) => conversation.conversationId === currentConversationId
      );
      if (!currentConversation) return null;

      const { isRead } = currentConversation.lastMessage;
      const isEditable = this.getIsEditable();

      return (
        <div style={{ margin: "1em", display: "inline-block" }}>
          <ReadIndicator
            messageRead={isRead}
            toggleCallBack={() => this.toggleReadStatus}
            allowHover={false}
            disabled={!isEditable}
            isHeader
          />
        </div>
      );
    };

    /**
     * Hide the details display
     */
    hideConversationDetails = () => {
      const {
        hideConversationDetails: hideConversationDetailsLocal,
        push,
      } = this.props;
      push(routeUrlBuilders.OPEN_CONVERSATION(this.props.currentConversationId || ''));
      // We don't change the redux state of conversation detail, because
      // the Conversation Details element is always mounted and hidden or shown.
      // hideConversationDetailsLocal(routes.TEXT_MESSAGES);
    };

    enableDetailButtons = () => {
      this.setState({ isDefaultMessageSelected: false });
    };

    disableDetailButtons = () => {
      this.setState({ isDefaultMessageSelected: true });
    };

    /**
     * Load the blocked phone numbers
     */
    loadBlockedNumbers = () => {
      const { fetchBlockedNumbers: fetchBlockedNumbersLocal } = this.props;
      fetchBlockedNumbersLocal();
    };

    /**
     * Request a getting user permissions
     */
    loadUserPermissions = () => {
      const { getUserPermissions: getUserPermissionsLocal } = this.props;
      getUserPermissionsLocal && getUserPermissionsLocal();
    };

    constructor(props) {
      super(props);

      this.state = {
        // Sets whether the delete confirmation dialog opened or not
        popupOpen: false,
        isDefaultMessageSelected: false,
        isShowBlockPermissionError: false,
        isCreateContactDialogOpen: false,
        isContactDialogOpen: false,
        alreadyInContacts: false,
        defaultContactData: {
          name: "",
          company: "",
          emails: [""],
          role: "",
          url: "",
          notes: "",
          id: "",
          phoneNumbers: [{
            phoneNumber: "",
            type: ContactPhoneTypeEnum.MOBILE,    
            id: "",
          }],
        }
      };
    }

    componentDidMount() {
      const { currentConversationId } = this.props;

      this.requestMessageCountUpdate();
      this.loadBlockedNumbers();
      this.loadUserPermissions();

      if (currentConversationId === WELCOME_CONVERSATION) {
        this.disableDetailButtons();
      } else {
        this.enableDetailButtons();
      }
    }

    componentDidUpdate(prevProps) {
      const { currentConversationId: prevCurrentConversationId } = prevProps;
      const { currentConversationId } = this.props;

      const conversationId = findConversationById({
        conversations: this.props.conversations,
        conversationId: this.props.conversationDetails,
      });
      const { contactNumber } = this.props.conversations[conversationId] ?? ""

      if (conversationId && this.state.defaultContactData.id != conversationId) {
        this.setState(state => ({
          ...state,
          defaultContactData: {
            ...state.defaultContactData,
            id: String(conversationId) ?? "",
            phoneNumbers: [{
              phoneNumber: contactNumber,
              type: ContactPhoneTypeEnum.MOBILE,    
              id: "",
            }],
          }
        }))
      }

      // If the conversationID changed, update the state of the callback button accordingly
      if (prevCurrentConversationId !== currentConversationId) {
        if (currentConversationId === WELCOME_CONVERSATION) {
          this.disableDetailButtons();
        } else {
          this.enableDetailButtons();
        }
      }
    }

    render() {
      const {
        conversationDetails,
        relatedCalls,
        parent,
        conversations,
        currentConversationId,
        temporaryConversation,
      } = this.props;


      const conversationId = findConversationById({
        conversations,
        conversationId: conversationDetails,
      });
      let conversation = conversations[conversationId];
      if (temporaryConversation && temporaryConversation.conversationId === currentConversationId) {
        conversation = temporaryConversation
      }
      if (conversation !== undefined) {
        const leftColumnWidth = 7;
        const rightColumnWidth = 9;
        const {
          contactNumber,
          talkrouteNumber,
          lastMessage,
          messagesOnServer,
        } = conversation;
        const formattedCallerNumber = formatPhoneNumber(contactNumber);
        const formattedVirtualnumber = formatPhoneNumber(talkrouteNumber);
        const sentAt = conversation.getSimpleFormattedDateAndTime();
        const { userEmail, body, attachments } = lastMessage;
        const sentBy = userEmail || formattedCallerNumber;

        let updatedBody = body;
        if (!updatedBody && attachments) {
          updatedBody = uiString.PHOTO_ATTACHMENT;
        }
        const lastMessageElement = (
          <List>
            <List.Item>
              <Grid>
                <Grid.Column
                  className="text-normal none-select-text"
                  width={leftColumnWidth}
                >
                  {`${uiString.conversationDetails.MESSAGE}: `}
                </Grid.Column>
                <Grid.Column
                  textAlign="right"
                  className="text-light"
                  width={rightColumnWidth}
                  style={overflowStyle}
                >
                  {updatedBody}
                </Grid.Column>
              </Grid>
            </List.Item>
            <List.Item style={{ paddingTop: "10px" }}>
              <Grid>
                <Grid.Column
                  className="text-normal none-select-text"
                  width={leftColumnWidth}
                >
                  {`${uiString.DATE_AND_TIME}: `}
                </Grid.Column>
                <Grid.Column
                  textAlign="right"
                  className="text-light"
                  width={rightColumnWidth}
                >
                  {sentAt}
                </Grid.Column>
              </Grid>
            </List.Item>
            <List.Item style={{ paddingTop: "10px" }}>
              <Grid>
                <Grid.Column
                  className="text-normal none-select-text"
                  width={leftColumnWidth}
                >
                  {`${uiString.conversationDetails.SENT_BY}: `}
                </Grid.Column>
                <Grid.Column
                  textAlign="right"
                  className="text-light"
                  width={rightColumnWidth}
                >
                  {sentBy}
                </Grid.Column>
              </Grid>
            </List.Item>
          </List>
        );

        return (
          <CallDetails
            title={uiString.CONVERSATION_DETAILS}
            relatedCalls={relatedCalls}
            parent={parent}
            buttons={this.drawHeaderButtons()}
            lastMessage={lastMessageElement}
            readIndicator={this.readIndicatorButton()}
            titleBlockHeaderStyle={{ marginBottom: 7 }}
          >
            {this.showBlockPermissionError()}
            <List>
              <List.Item>
                <Grid>
                  <Grid.Column
                    className="text-normal none-select-text"
                    width={leftColumnWidth}
                  >
                    {`${uiString.conversationDetails.TO}: `}
                  </Grid.Column>
                  <Grid.Column
                    textAlign="right"
                    className="text-light"
                    width={rightColumnWidth}
                  >
                    {formattedCallerNumber}
                  </Grid.Column>
                </Grid>
              </List.Item>
              <List.Item style={{ paddingTop: "10px" }}>
                <Grid>
                  <Grid.Column
                    className="text-normal none-select-text"
                    width={leftColumnWidth}
                  >
                    {`${uiString.conversationDetails.VIRTUAL_PHONE_NUMBER}: `}
                  </Grid.Column>
                  <Grid.Column
                    textAlign="right"
                    className="text-light"
                    width={rightColumnWidth}
                  >
                    {formattedVirtualnumber}
                  </Grid.Column>
                </Grid>
              </List.Item>
              <List.Item style={{ paddingTop: "10px" }}>
                <Grid>
                  <Grid.Column
                    className="text-normal none-select-text"
                    width={leftColumnWidth}
                  >
                    {`${uiString.conversationDetails.MESSAGE_COUNT}: `}
                  </Grid.Column>
                  <Grid.Column
                    textAlign="right"
                    className="text-light"
                    width={rightColumnWidth}
                  >
                    {messagesOnServer === null
                      ? uiString.conversationDetails.N_A
                      : messagesOnServer}
                  </Grid.Column>
                </Grid>
              </List.Item>
            </List>
            <DeleteConversationDialog
              open={this.state.popupOpen}
              conversations={conversations}
              currentConversationId={currentConversationId}
              onClose={this.cancelDeletingConversation}
              onDelete={this.deleteConversation}
            />
            <ContactDialog
              open={this.state.isContactDialogOpen}
              onClose={() => this.setState({ isContactDialogOpen: false })}
              phoneNumber={contactNumber}
            />
            <ContactCreateDialogManagement 
              open={this.state.isCreateContactDialogOpen}
              close={() => this.setState({ isCreateContactDialogOpen: false })}
              defaultContactData={this.state.defaultContactData as IContact}
              conversationId={this.props.conversationDetails}
              conversationList={this.props.conversations}
              onContactAvailable={contact => this.setState({alreadyInContacts: Boolean(contact)})}
            />
          </CallDetails>
        );
      }

      return null;
    }
  }

  const ContactCreateDialogManagement = ({open, close, conversationId, onContactAvailable, defaultContactData, conversationList} :
    {
      open: boolean,
      close: () => void,
      conversationId: string | undefined | null,
      onContactAvailable: (contact?: IContact) => void,
      defaultContactData: IContact,
      conversationList: ConversationData[]
    }) => {
    const {data: contactList} = useGetAllContactsQuery({ params: { 
      pageSize: GET_TEN_THOUSAND_CONTACTS,
      page: CONTACTS_INITIAL_PAGE
    } });
    const conversation = conversationList.find(c => c.conversationId === conversationId);
    const conversationContact = useMemo(() => contactList && conversation ?
    addContactNameToConversation(contactList, [conversation])[0]?.contact:
    conversation?.contact, [contactList, conversation])

    useEffect(() => {
      onContactAvailable(conversationContact)
    }, [conversationContact])

    return <>
          <CreateContactDialog
            open={open}
            onClose={close}
            contactData={conversationContact ? conversationContact : defaultContactData}
          />
    </>
  }
  
  /**
   * Map props to a redux state
   * @param {*} state
   */
  const mapStateToProps = (state) => ({
    conversationDetails: state.conversations.conversationDetails,
    conversations: state.conversations.data,
    temporaryConversation: state.conversations.temporaryConversation,
    relatedCalls: state.conversations.relatedCalls,
    parent: state.conversations.conversationDetailsParent,
    currentConversationId: state.conversations.selectedConversationId,
    blockedNumbers: state.callReducer.blockedNumbers,
    userPermissions: state.authentication.userPermissions,
    outgoingPhoneNumbers: state.preferences.outgoingPhoneNumbers,
  });

  export default connect(mapStateToProps, {
    push,
    hideConversationDetails,
    updateMessageCount,
    updateReadStatus,
    deleteConversation,
    setOutgoingPhoneNumber,
    setNumberToCall,
    fetchBlockedNumbers,
    getUserPermissions,
    blockNumber,
    unblockNumber,
  })(ConversationDetails);
